import { workProductCardClassName } from '@/components/work-product-card/work-product-card-styles'
import { WorkProductCardContents } from '@/components/work-product-card/WorkProductCardParts'

import { ArrowUpRight, DraftSparkle } from '@/assets/icons'
import { FileChip } from '@/components/file-chip/FileChip'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import { MatterArtifactVersionStatus, type MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { kMatterArtifactNameFallback } from '@/matter-artifacts/constants'
import { selectOpenMatterArtifactId } from '@/matter-artifacts/store/selectors'
import { useAppSelector } from '@/store/store-hooks'

type MatterArtifactButtonProps = {
  matterArtifactVersion: MatterArtifactVersionRead
  /* A newer version of the artifact has its own button later in the chat. */
  isOutdated: boolean
  canCancelGenerating: boolean
  onCancelGenerating: () => void
  onViewMatterArtifact: (matterArtifactVersion: MatterArtifactVersionRead) => void
  onViewCurrentMatterArtifact: (matterArtifactVersion: MatterArtifactVersionRead) => void
}

export default function MatterArtifactButton({
  matterArtifactVersion,
  isOutdated,
  canCancelGenerating,
  onCancelGenerating,
  onViewMatterArtifact,
  onViewCurrentMatterArtifact,
}: MatterArtifactButtonProps) {
  const { draftArtifactVersionId } = useDraftWorkspace()
  const openMatterArtifactId = useAppSelector((state) => selectOpenMatterArtifactId(state, { draftArtifactVersionId }))
  // Compared by artifact, since opening a draft opens its current version, which can be newer than the version this button was made for.
  const isActive = openMatterArtifactId === matterArtifactVersion.matter_artifact_id
  const name = matterArtifactVersion.name || kMatterArtifactNameFallback

  switch (matterArtifactVersion.status) {
    case MatterArtifactVersionStatus.GENERATING:
      return (
        <div className="grid grid-cols-[60px_minmax(0,1fr)] items-center min-h-[62px] w-full max-w-[420px] rounded-[5px] overflow-hidden bg-brand-neutral-50 border border-brand-neutral-300">
          <div className="bg-brand-neutral-100 flex items-center justify-center w-full h-full border-r border-brand-neutral-300">
            <CircularProgressContinuousSized size={20} thickness={4} showTrack />
          </div>
          <div className="flex justify-between flex-wrap gap-x-2 gap-y-1 p-3 items-baseline">
            <div className="text-[13px] text-left font-medium leading-tight">Generating...</div>
            {canCancelGenerating ? (
              <button
                onClick={onCancelGenerating}
                aria-label="Cancel generating document"
                className="leading-none text-[12px] text-red-700 font-normal cursor-pointer inline"
              >
                Cancel
              </button>
            ) : (
              <div>&nbsp;</div>
            )}
          </div>
        </div>
      )

    case MatterArtifactVersionStatus.ERROR:
    case MatterArtifactVersionStatus.CANCELLED:
      return null

    case MatterArtifactVersionStatus.COMPLETE:
      if (isOutdated) {
        return (
          // Same height as the full button, so the chat doesn't shift when a button becomes outdated.
          <div className="flex min-h-[62px] w-full max-w-[420px] flex-col items-start justify-center gap-1">
            <FileChip name={name} variant="draft" size="large" disabled />
            <button
              type="button"
              onClick={() => onViewCurrentMatterArtifact(matterArtifactVersion)}
              aria-label={`View current version of ${name}`}
              className="flex items-center gap-1 text-sm font-medium leading-5 tracking-[-0.15px] text-brand-700 underline transition-colors duration-150 hover-capable:hover:text-brand-800 focus:outline-hidden focus-visible:outline focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
            >
              <ArrowUpRight className="size-4 shrink-0" />
              View Current Version
            </button>
          </div>
        )
      }

      return (
        <button
          onClick={() => onViewMatterArtifact(matterArtifactVersion)}
          data-work-product-card
          className={workProductCardClassName(isActive)}
          aria-label={isActive ? `Viewing artifact: ${name}` : `View artifact: ${name}`}
          aria-current={isActive ? 'true' : undefined}
          data-matter-artifact-version-id={matterArtifactVersion.id}
        >
          <WorkProductCardContents activeLabel="Viewing" active={isActive} icon={<DraftSparkle className="w-6 h-6" />} title={name} />
        </button>
      )
  }
}
